import { memo, ReactNode, useCallback, useEffect, useState } from 'react';
import classes from 'classnames';
import { LoadableComponentMethods } from '@loadable/component';
import { twMerge } from 'tailwind-merge';
import * as R from '@/Router/routes';
import MobileMenu, {
  MenuItem,
} from '@/components/Layouts/components/MobileHeader/MobileMenu';
import Heading from '@/components/Heading/Heading';
import Link from '@/components/Link/Link';
import Portal from '@/components/Portal/Portal';
import styles from './MobileHeader.module.scss';
import useBreakpoint from '@/hooks/useBreakpoint';

import { AsyncActiveLoan } from '@/pages/ActiveLoan/async';
import ChevronIcon from '@/assets/icons/chevron-sharp.svg?react';
import FullLogo from '@/assets/icons/logo.svg?react';
import Logo from '@/assets/icons/logo-no-text.svg?react';
import Menu from '@/assets/icons/menu.svg?react';

export type Props = {
  logoLinkUrl?: string;
  homePreload?: LoadableComponentMethods<unknown>['preload'];
  backUrl?: string;
  backPreload?: LoadableComponentMethods<unknown>['preload'];
  heading?: string | ReactNode;
  menuItems: MenuItem[];
  className?: string;
  onLogout?: () => void;
  clientName?: string;
  clientContract?: string;
  pending?: boolean;
  withMenu?: boolean;
};

const MobileHeaderMemo = memo(function MobileHeader({
  logoLinkUrl = R.ACTIVE_LOAN,
  homePreload = AsyncActiveLoan.preload,
  backUrl,
  backPreload,
  heading,
  menuItems,
  className,
  onLogout,
  clientName,
  clientContract,
  pending,
  withMenu = true,
}: Props) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { size } = useBreakpoint();

  useEffect(() => {
    if (size.index > 2) {
      setIsMobileMenuOpen(false);
    }
  }, [size.index]);

  useEffect(() => {
    backPreload?.();
    homePreload?.();
  }, [homePreload, backPreload]);

  const handleMenuToggle = useCallback(() => {
    setIsMobileMenuOpen((prevState) => !prevState);
  }, []);

  return (
    <>
      <header
        className={twMerge(
          'lg:bg-background flex justify-between px-0 p-5 w-full',
          className,
        )}
        data-test="mobileHeader-header"
      >
        <div className={styles.item}>
          {backUrl ? (
            <Link
              to={backUrl}
              dataTest="mobile-back-link"
              className={styles.back}
            >
              <ChevronIcon className={styles.chevron} />
            </Link>
          ) : (
            withMenu && (
              <Link to={logoLinkUrl} dataTest="mobile-back-link">
                <Logo data-test="mobile-logo" width={25} />
              </Link>
            )
          )}
        </div>

        <div className={classes(styles.item, styles.center)}>
          {withMenu ? (
            <Heading
              className={classes(styles['mobile-heading'])}
              dataTest="mobileHeader-heading"
            >
              {heading}
            </Heading>
          ) : (
            <Link to={logoLinkUrl} dataTest="mobileHeader-logo-link">
              <FullLogo data-test="mobile-logo" />
            </Link>
          )}
        </div>
        <div className={classes(styles.item, styles.right)}>
          {withMenu && (
            <div
              className={styles['menu-button']}
              onClick={handleMenuToggle}
              onKeyDown={handleMenuToggle}
              tabIndex={0}
              role="button"
              aria-label="mobile-menu-toggle"
            >
              <Menu data-test="mobileHeader-menu-logo" />
            </div>
          )}
        </div>
      </header>

      {isMobileMenuOpen && (
        <Portal>
          <MobileMenu
            className={styles.menu}
            onSelect={handleMenuToggle}
            onClose={handleMenuToggle}
            menuItems={menuItems}
            active={window.location.pathname}
            clientName={clientName}
            clientContract={clientContract}
            onLogout={onLogout}
            pending={pending}
          />
        </Portal>
      )}
    </>
  );
});

export default MobileHeaderMemo;
